import type { Editor } from '@tiptap/react';
import { normalizeLinkHref } from './link-href';

export interface ApplyLinkArgs {
  /** Raw user-entered href; passes through `normalizeLinkHref` before use. */
  href: string;
  /** Label text the user wants displayed; trimmed; falls back to the normalised href. */
  text: string;
}

/**
 * Runs the right command chain for the toolbar Link popover. Three branches:
 *
 * 1. Cursor is inside an existing link mark → extend across the mark, replace its text,
 *    and reapply the link attrs (`target`/`rel` come from the Link extension defaults).
 * 2. Selection is non-empty → insertContentAt replaces the selection with the linked label.
 * 3. Selection is empty → insertContentAt inserts the linked label at the cursor.
 *
 * Returns true when the chain ran; false when the href could not be normalised into a
 * safe target.
 */
export const applyLink = (editor: Editor, { href, text }: ApplyLinkArgs): boolean => {
  const normalized = normalizeLinkHref(href);
  if (!normalized) {
    return false;
  }

  const label = text.trim() || normalized;

  if (editor.isActive('link')) {
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .command(({ tr }) => {
        const { from, to } = tr.selection;
        tr.insertText(label, from, to);
        return true;
      })
      .setLink({ href: normalized })
      .run();
    return true;
  }

  const { from, to } = editor.state.selection;
  editor
    .chain()
    .focus()
    .insertContentAt(
      { from, to },
      {
        type: 'text',
        text: label,
        marks: [{ type: 'link', attrs: { href: normalized } }],
      }
    )
    .run();
  return true;
};
